// src/pages/Counselling/Requests/Components/RejectRequestModal.tsx
/**
 * Rejecting a care request.
 *
 * Mirrors the decline half of `PendingCases/Components/PendingCaseModal.tsx`: a reason is required, and
 * the submit stays disabled until one is given.
 *
 * ⚠️ Unlike that modal, there is **no note-then-decline dance and no `encodeNote`**. `reason` is a
 * first-class field on `CareRequestRejectRequest`, so it is sent plainly and stored on the request.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Modal, Stack, Switch, Text, Textarea } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useCareRequestService, type CareRequestModelView } from '@actschurch/ccms-sdk';

export interface RejectRequestModalProps {
  request: CareRequestModelView | null;
  opened: boolean;
  onClose: () => void;
  onRejected: () => void;
}

export default function RejectRequestModal({
  request,
  opened,
  onClose,
  onRejected,
}: RejectRequestModalProps) {
  const requestService = useCareRequestService();

  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  /** Defaults to true, matching the SDK's own default for `sendReferralMessage`. */
  const [sendReferral, setSendReferral] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setReason('');
    setTouched(false);
    setSendReferral(true);
  }, [opened]);

  const handleReject = async () => {
    const trimmed = reason.trim();
    if (!trimmed) { setTouched(true); return; }
    if (!request || busy) return;

    setBusy(true);
    try {
      await requestService.reject(request.requestID, {
        reason: trimmed,
        sendReferralMessage: sendReferral,
      });
      toast.success('Request rejected', `${request.reference ?? `Request #${request.requestID}`} was closed.`);
      onRejected();
      onClose();
    } catch (e) {
      console.error('[RejectRequest] reject failed:', e);
      toast.error('Could not reject', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title="Reject request"
      size="lg"
      centered
    >
      {request && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {request.reference ?? `Request #${request.requestID}`} from{' '}
            <Text span fw={600}>{request.fullName ?? 'Unknown'}</Text>. No case will be created.
          </Text>

          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Textarea
              label="Reason"
              placeholder="Why is this request not being taken up?"
              autosize
              minRows={3}
              data-autofocus
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              onBlur={() => setTouched(true)}
              error={touched && !reason.trim() ? 'A reason is required to reject.' : undefined}
            />
          </Alert>

          <Switch
            label="Send the requester a decline message with referral guidance"
            checked={sendReferral}
            onChange={(e) => setSendReferral(e.currentTarget.checked)}
            description={sendReferral
              ? 'They will be told their request was not taken up, and pointed to other support.'
              : 'The requester will not hear anything back.'}
          />

          {/* The referral message is a gentle decline with guidance; it does not carry the reason. */}
          <Text size="xs" c="dimmed">
            The reason is stored on the request for the care office. It is not included in the message
            sent to the requester.
          </Text>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button color="red" onClick={handleReject} loading={busy} disabled={!reason.trim()}>
              Reject request
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
